import React, { useState, useMemo } from 'react';
import { ProductFilters } from '@/app/components/ProductFilters';
import { ProductCard } from '@/app/components/ProductCard';
import { SizeGuideModal } from '@/app/components/SizeGuideModal';
import { Product, PRODUCTS } from '@/app/data';
import { Search } from 'lucide-react';

interface PLPProps {
  onAddToCart: (product: Product) => void;
  onProductClick: (product: Product) => void;
  initialTypeFilter?: string | null;
  onClearTypeFilter?: () => void;
}

export const PLP: React.FC<PLPProps> = ({ onAddToCart, onProductClick, initialTypeFilter, onClearTypeFilter }) => {
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const [helmetTypeFilter, setHelmetTypeFilter] = useState<string | null>(initialTypeFilter || null);

  // Update helmet type filter when initialTypeFilter changes
  React.useEffect(() => {
    if (initialTypeFilter) {
      setHelmetTypeFilter(initialTypeFilter);
      setActiveCategory('Todos'); // Reset category when type filter is applied
    }
  }, [initialTypeFilter]);

  const filteredProducts = useMemo(() => {
    return PRODUCTS.filter((p: Product) => {
      // When "Trabajo" is selected, also include all "Modular" type products
      const matchesCategory = activeCategory === 'Todos' || 
                             p.category.includes(activeCategory as any) ||
                             (activeCategory === 'Trabajo' && p.type === 'Modular');
      const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = !helmetTypeFilter || p.type === helmetTypeFilter;
      return matchesCategory && matchesSearch && matchesType;
    });
  }, [activeCategory, searchQuery, helmetTypeFilter]);

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-[#F9FAFB]">
      {/* Mobile Filters */}
      <div className="md:hidden sticky top-0 z-30 bg-white shadow-sm">
         <ProductFilters 
            isMobile={true} 
            activeCategory={activeCategory} 
            onSelectCategory={setActiveCategory} 
         />
      </div>

      {/* Desktop Filters Sidebar */}
      <div className="hidden md:block sticky top-[80px] h-[calc(100vh-80px)] overflow-y-auto">
        <ProductFilters 
            activeCategory={activeCategory} 
            onSelectCategory={setActiveCategory}
            onOpenSizeGuide={() => setIsSizeGuideOpen(true)}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 md:p-8">
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
                <h1 className="text-4xl font-black italic text-[#061970] uppercase tracking-tighter mb-2">
                    {helmetTypeFilter 
                      ? `Cascos ${helmetTypeFilter}` 
                      : activeCategory === 'Todos' ? 'Catálogo Completo' : `Colección ${activeCategory}`}
                </h1>
                <p className="text-gray-500 font-medium">
                    {filteredProducts.length} {filteredProducts.length === 1 ? 'Producto' : 'Productos'} encontrados
                </p>
                {helmetTypeFilter && (
                  <button
                    onClick={() => {
                      setHelmetTypeFilter(null);
                      onClearTypeFilter?.();
                    }}
                    className="mt-2 text-sm text-[#FF7F32] hover:text-[#e66a20] font-bold underline"
                  >
                    Limpiar filtro de tipo
                  </button>
                )}
            </div>

            {/* Search */}
            <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input 
                    type="text" 
                    placeholder="Buscar modelo..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 rounded-full bg-white border border-gray-200 focus:border-[#459EDF] outline-none text-sm font-bold text-[#061970] shadow-sm"
                />
            </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product: Product) => (
                <ProductCard 
                    key={product.id} 
                    product={product} 
                    onAddToCart={onAddToCart}
                    onClick={onProductClick}
                />
            ))}
        </div>
        
        {filteredProducts.length === 0 && (
            <div className="text-center py-20 opacity-50">
                <p className="text-xl font-bold italic text-[#061970]">No se encontraron productos.</p>
            </div>
        )}
      </div>

      <SizeGuideModal isOpen={isSizeGuideOpen} onClose={() => setIsSizeGuideOpen(false)} />
    </div>
  );
};
